import React from 'react'

import { ChartData } from '@/Atomic/Organisms/Charts/acBar/dataConverter'
import {
  ColumnType,
  DateFormat,
  formatColumnValue,
  NumberFormat,
} from '@/Atomic/Organisms/Charts/formatChartData'
import { getCurrentLocale } from '@/utils/localeMapping'

import './Watchlist.scss'

/**
 * Fallback formatter for columns without an explicit format definition:
 * converts ISO datetime strings to the user's timezone and locale, otherwise
 * returns the value unchanged.
 */
function formatValue(value: string | null, locale: string): string {
  if (!value) return ''

  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/
  if (isoDateRegex.test(value)) {
    try {
      const date = new Date(value)
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return value
    }
  }

  return value
}

interface V2ListItem {
  data: string[]
}

interface V2ColumnStyle {
  foreground?: string
  font?: string
  format?: DateFormat & NumberFormat
}

interface Param {
  salesData: ChartData | undefined
}

export function Watchlist({ salesData = undefined }: Param) {
  if (!salesData) {
    return (
      <div className="watchlist">
        <div className="no-data">
          <div>No data found</div>
        </div>
      </div>
    )
  }

  const metaData = salesData.metaData
  if (!metaData) {
    return (
      <div className="watchlist">
        <div className="no-data">
          <div>No data found</div>
        </div>
      </div>
    )
  }

  const items: V2ListItem[] = salesData.listData || []

  if (!items || !items.length) {
    return (
      <div className="watchlist">
        <div className="no-data">
          <div>No data found</div>
        </div>
      </div>
    )
  }

  let rowLayout: number[][]
  let columnStyles: V2ColumnStyle[]
  let columnTypes: ColumnType[]

  try {
    // Get rowLayout or create default (all columns in one row)
    rowLayout = metaData.properties?.rowLayout || []
    if (!rowLayout || rowLayout.length === 0) {
      // Create default layout: all columns in a single row
      const numColumns = items[0]?.data?.length || 0
      rowLayout = [Array.from({ length: numColumns }, (_, i) => i)]
    }

    columnStyles = metaData.properties?.columnStyles || []
    columnTypes = metaData.properties?.columnTypes || []
  } catch (error) {
    console.error('Error processing watchlist data:', error, salesData)
    return (
      <div className="watchlist">
        <div className="no-data">
          <div>Unable to render list</div>
        </div>
      </div>
    )
  }

  const locale = getCurrentLocale()

  return (
    <div className="watchlist">
      <div className="list">
        {items.map((item: V2ListItem, index: number) => (
          <div key={index} className="wl-item">
            {rowLayout.map((row: number[], rowIndex: number) => (
              <div key={rowIndex}>
                {row
                  .map((colIndex: number, colIdx: number) => {
                    let textStyle: React.CSSProperties = {}
                    const colStyle = columnStyles?.[colIndex]
                    if (colStyle) {
                      textStyle = {
                        color: colStyle.foreground || 'inherit',
                        fontWeight:
                          colStyle.font === 'bold' ? 'bold' : 'normal',
                      }
                    }

                    const format = colStyle?.format
                    const columnType = columnTypes[colIndex]
                    const rawValue = item.data[colIndex]
                    const displayValue =
                      format || columnType
                        ? formatColumnValue(
                            rawValue,
                            columnType,
                            format,
                            locale,
                          )
                        : formatValue(rawValue ?? null, locale)

                    return rawValue ? (
                      <span
                        key={colIdx}
                        className="wl-value-element"
                        style={textStyle}
                      >
                        {displayValue}
                      </span>
                    ) : null
                  })
                  .filter((item): item is React.ReactElement => Boolean(item))
                  .reduce(
                    (
                      acc: React.ReactElement[],
                      curr: React.ReactElement,
                      idx: number,
                      arr: React.ReactElement[],
                    ) => {
                      acc.push(curr)
                      if (idx < arr.length - 1) {
                        acc.push(
                          <span
                            className="wl-value-space"
                            key={'wl-value-space-' + idx}
                          >
                            &#x2022;
                          </span>,
                        )
                      }
                      return acc
                    },
                    [] as React.ReactElement[],
                  )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
